// frontend/src/pages/Companies/[id].tsx

import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Card } from '@/components/common/UI/Card'
import { Spinner } from '@/components/common/UI/Spinner'
import { VacancyCard } from '@/components/vacancy/VacancyCard'
import { getInitialsAvatar, generateRandomColor } from '@/utils/helpers'
import { useAuthStore } from '@/store'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'

const CompanyTeamPanel: React.FC<{ companyId: number }> = ({ companyId }) => {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const team = useQuery({
    queryKey: ['company', companyId, 'members'],
    queryFn: () => apiClient.get(`/v1/companies/${companyId}/members`),
  })
  const invite = useMutation({
    mutationFn: () => apiClient.post(`/v1/companies/${companyId}/members/invite`, { email }),
    onSuccess: () => {
      setEmail('')
      queryClient.invalidateQueries({ queryKey: ['company', companyId, 'members'] })
    },
  })
  const remove = useMutation({
    mutationFn: (userId: number) => apiClient.delete(`/v1/companies/${companyId}/members/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company', companyId, 'members'] }),
  })

  return (
    <Card className="mb-8 p-5">
      <h2 className="text-lg font-semibold">Команда найма</h2>
      <p className="mt-1 text-sm text-secondary-600">Пригласите работодателя по email. Участники компании смогут просматривать отклики, менять этапы и комментировать кандидатов.</p>
      <form className="mt-4 flex flex-col gap-2 sm:flex-row" onSubmit={(event) => { event.preventDefault(); invite.mutate() }}>
        <Input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="hr@example.com" aria-label="Email работодателя" />
        <Button type="submit" disabled={invite.isPending}>{invite.isPending ? 'Добавляем…' : 'Добавить в команду'}</Button>
      </form>
      {invite.isError && <p className="mt-2 text-sm text-error">Не удалось добавить пользователя. Проверьте email и права владельца компании.</p>}
      {team.data?.length > 0 && <ul className="mt-4 divide-y divide-secondary-100">{team.data.map((member: any) => <li key={member.user.id} className="flex items-center justify-between py-2 text-sm">
        <span>{member.user.full_name || member.user.email} <span className="text-secondary-500">· {member.role}</span></span>
        <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(member.user.id)}>Удалить</Button>
      </li>)}</ul>}
    </Card>
  )
}

const ActiveSearchPanel: React.FC<{ companyId: number; vacancies: any[] }> = ({ companyId, vacancies }) => {
  const queryClient = useQueryClient()
  const [vacancyId, setVacancyId] = useState<number | ''>('')
  const [title, setTitle] = useState('')
  const [keywords, setKeywords] = useState('')
  const [city, setCity] = useState('')
  const [experience, setExperience] = useState('')
  const [message, setMessage] = useState('Здравствуйте! Ваше резюме подходит для нашей вакансии. Будем рады обсудить её с вами.')
  const rules = useQuery({
    queryKey: ['active-search', companyId],
    queryFn: () => apiClient.get(`/v1/active-search/companies/${companyId}`),
  })
  const save = useMutation({
    mutationFn: () => apiClient.post('/v1/active-search/rules', {
      company_id: companyId,
      vacancy_id: Number(vacancyId),
      title,
      keywords: keywords.split(',').map((value) => value.trim()).filter(Boolean),
      city: city.trim() || null,
      experience_level: experience || null,
      invitation_message: message,
    }),
    onSuccess: () => {
      setTitle('')
      setKeywords('')
      queryClient.invalidateQueries({ queryKey: ['active-search', companyId] })
    },
  })
  const update = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => apiClient.patch(`/v1/active-search/rules/${id}`, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['active-search', companyId] }),
  })
  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/v1/active-search/rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['active-search', companyId] }),
  })
  const activeVacancies = vacancies.filter((vacancy) => vacancy.is_active)

  return <Card className="mb-8 p-5">
    <h2 className="text-lg font-semibold">Автопоиск кандидатов</h2>
    <p className="mt-1 text-sm text-secondary-600">Каждые 15 минут система проверяет новые публичные резюме и отправляет подходящим кандидатам приглашение. Лимит — 50 приглашений в сутки на правило.</p>
    <form className="mt-4 grid gap-3 md:grid-cols-2" onSubmit={(event) => { event.preventDefault(); save.mutate() }}>
      <select required value={vacancyId} onChange={(event) => setVacancyId(Number(event.target.value))} className="rounded border border-secondary-300 px-3 py-2" aria-label="Вакансия">
        <option value="">Выберите вакансию</option>
        {activeVacancies.map((vacancy) => <option key={vacancy.id} value={vacancy.id}>{vacancy.title}</option>)}
      </select>
      <Input required minLength={2} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Например, Junior React в Москве" aria-label="Название правила" />
      <Input value={keywords} onChange={(event) => setKeywords(event.target.value)} placeholder="Ключевые слова через запятую" aria-label="Ключевые слова" />
      <Input value={city} onChange={(event) => setCity(event.target.value)} placeholder="Город (необязательно)" aria-label="Город" />
      <select value={experience} onChange={(event) => setExperience(event.target.value)} className="rounded border border-secondary-300 px-3 py-2" aria-label="Опыт кандидата">
        <option value="">Любой опыт</option>
        <option value="no_experience">Без опыта</option>
        <option value="less_1_year">Менее года</option>
        <option value="from_1_to_3">1–3 года</option>
        <option value="from_3_to_6">3–6 лет</option>
        <option value="more_6">Более 6 лет</option>
      </select>
      <textarea required minLength={10} maxLength={1000} value={message} onChange={(event) => setMessage(event.target.value)} className="min-h-24 rounded border border-secondary-300 px-3 py-2 md:col-span-2" aria-label="Текст приглашения" />
      <Button type="submit" disabled={save.isPending || activeVacancies.length === 0} className="md:col-span-2">{save.isPending ? 'Сохраняем…' : 'Создать правило'}</Button>
    </form>
    {save.isError && <p className="mt-2 text-sm text-error">Не удалось сохранить правило. Проверьте выбранную вакансию.</p>}
    <ul className="mt-4 divide-y divide-secondary-100">{(rules.data || []).map((rule: any) => <li key={rule.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
      <span><strong>{rule.title}</strong> · {rule.city || 'любой город'} · {rule.is_active ? 'включено' : 'на паузе'}</span>
      <span className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: rule.id, is_active: !rule.is_active })}>{rule.is_active ? 'Пауза' : 'Включить'}</Button>
        <Button size="sm" variant="ghost" onClick={() => remove.mutate(rule.id)}>Удалить</Button>
      </span>
    </li>)}</ul>
  </Card>
}

const CandidateAlertSubscriptions: React.FC = () => {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [query, setQuery] = useState('')
  const [city, setCity] = useState('')
  const [keywords, setKeywords] = useState('')
  const subscriptions = useQuery({ queryKey: ['subscriptions'], queryFn: () => apiClient.get<any[]>('/v1/subscriptions') })
  const create = useMutation({ mutationFn: () => apiClient.post('/v1/subscriptions', { title: title.trim(), filters: { query: query.trim(), city: city.trim(), keywords: keywords.split(',').map(value => value.trim()).filter(Boolean) } }), onSuccess: () => { setTitle(''); queryClient.invalidateQueries({ queryKey: ['subscriptions'] }) } })
  const update = useMutation({ mutationFn: ({ id, is_active }: any) => apiClient.patch(`/v1/subscriptions/${id}`, { is_active }), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }) })
  const remove = useMutation({ mutationFn: (id: number) => apiClient.delete(`/v1/subscriptions/${id}`), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }) })
  return <Card className="mb-8 p-5"><h2 className="text-lg font-semibold">Email-уведомления о новых резюме</h2><p className="mt-1 text-sm text-secondary-600">Рассылка примерно раз в 15 минут включает только новые публичные профили по выбранным фильтрам.</p><form className="mt-4 grid gap-2 sm:grid-cols-2" onSubmit={event => { event.preventDefault(); create.mutate() }}><Input required minLength={2} maxLength={120} value={title} onChange={event => setTitle(event.target.value)} placeholder="Название фильтра" aria-label="Название подписки"/><Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Должность или специализация" aria-label="Поисковая фраза"/><Input value={city} onChange={event => setCity(event.target.value)} placeholder="Город" aria-label="Город"/><Input value={keywords} onChange={event => setKeywords(event.target.value)} placeholder="Навыки через запятую" aria-label="Навыки"/><Button type="submit" className="sm:col-span-2" disabled={create.isPending}>Подписаться на новые резюме</Button></form>{create.isError && <p className="mt-2 text-sm text-error">Не удалось создать подписку.</p>}<ul className="mt-3 divide-y divide-secondary-100">{(subscriptions.data || []).map((item: any) => <li key={item.id} className="flex flex-wrap justify-between gap-2 py-2 text-sm"><span>{item.title} · {item.is_active ? 'включена' : 'пауза'}</span><span className="flex gap-3"><button className="text-primary-700 underline" onClick={() => update.mutate({ id: item.id, is_active: !item.is_active })}>{item.is_active ? 'Пауза' : 'Включить'}</button><button className="text-secondary-600 underline" onClick={() => remove.mutate(item.id)}>Удалить</button></span></li>)}</ul></Card>
}

const CandidateCompanyActions: React.FC<{ company: any; companyId: string }> = ({ company, companyId }) => {
  const queryClient = useQueryClient()
  const subscriptions = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => apiClient.get<any[]>('/v1/subscriptions'),
  })
  const existing = (subscriptions.data || []).find((item: any) => Number(item.filters?.company_id) === Number(companyId))
  const subscribe = useMutation({
    mutationFn: () => apiClient.post('/v1/subscriptions', {
      title: `Вакансии: ${company.name}`.slice(0, 120),
      filters: { company_id: Number(companyId) },
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
  })
  const unsubscribe = useMutation({
    mutationFn: () => apiClient.delete(`/v1/subscriptions/${existing.id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
  })
  return <div className="flex flex-wrap gap-2">
    <Button variant="outline" onClick={() => document.getElementById('company-vacancies')?.scrollIntoView({ behavior: 'smooth' })}>Хочу тут работать</Button>
    <Button disabled={subscribe.isPending || unsubscribe.isPending} onClick={() => existing ? unsubscribe.mutate() : subscribe.mutate()}>
      {existing ? 'Отписаться от вакансий' : 'Подписаться на вакансии'}
    </Button>
    {(subscribe.isError || unsubscribe.isError) && <p className="w-full text-xs text-error">Не удалось изменить подписку. Возможно, достигнут лимит вашего тарифа.</p>}
  </div>
}

const CompanyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()

  const { data: company, isLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      return apiClient.get(`/v1/companies/${id}`)
    }
  })

  const { data: vacancies } = useQuery({
    queryKey: ['company', id, 'vacancies'],
    queryFn: async () => {
      return apiClient.get(`/v1/companies/${id}/vacancies`)
    },
    enabled: !!company
  })
  const { data: rating } = useQuery({
    queryKey: ['reviews', 'company', id],
    queryFn: () => apiClient.get<any>(`/v1/reviews/companies/${id}`),
    enabled: !!company,
  })

  if (isLoading) {
    return (
      <div className="container-custom py-12 flex justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!company) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-secondary-500">Компания не найдена</p>
      </div>
    )
  }

  const avatarColor = generateRandomColor()
  const initials = getInitialsAvatar(company.name)

  return (
    <div className="container-custom py-8">
      {/* Header */}
      <Card className="p-6 mb-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Logo */}
          {company.logo_url ? (
            <img
              src={company.logo_url}
              alt={company.name}
              className="w-24 h-24 rounded-lg object-cover"
            />
          ) : (
            <div
              className="w-24 h-24 rounded-lg flex items-center justify-center text-3xl font-bold text-white"
              style={{ backgroundColor: avatarColor }}
            >
              {initials}
            </div>
          )}

          <div className="flex-1">
            <h1 className="text-2xl font-bold">{company.name}</h1>
            {rating?.ratings_count > 0 && <p className="mt-1 text-sm"><span className="text-amber-500">{'★'.repeat(rating.stars)}{'☆'.repeat(5-rating.stars)}</span> <strong>{rating.average_rating}</strong> <span className="text-secondary-500">· {rating.ratings_count} отзывов соискателей</span></p>}
            {company.industry && (
              <p className="text-secondary-600">{company.industry}</p>
            )}
            {company.employees_count && (
              <p className="text-sm text-secondary-500">
                {company.employees_count} сотрудников
              </p>
            )}
            {company.is_verified && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Проверенная компания
              </span>
            )}
          </div>

          <div className="flex flex-col items-start gap-3 sm:items-end">
            {company.website && (
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 text-sm"
              >
                🌐 Сайт
              </a>
            )}
            {user?.role === 'candidate' && <CandidateCompanyActions company={company} companyId={id || ''} />}
          </div>
        </div>

        {/* Description */}
        {company.description && (
          <div className="mt-4">
            <h3 className="font-semibold mb-2">О компании</h3>
            <p className="text-secondary-600 text-sm whitespace-pre-wrap">
              {company.description}
            </p>
          </div>
        )}

        {company.benefits?.length > 0 && <div className="mt-5"><h3 className="mb-2 font-semibold">Преимущества работы у нас</h3><ul className="grid gap-2 text-sm text-secondary-600 sm:grid-cols-2">{company.benefits.map((benefit: string, index: number) => <li key={`${benefit}-${index}`} className="rounded-lg bg-secondary-50 px-3 py-2">✓ {benefit}</li>)}</ul></div>}

        {/* Contacts */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {company.email && (
            <div>
              <span className="text-secondary-500">Email:</span>
              <span className="ml-2">{company.email}</span>
            </div>
          )}
          {company.phone && (
            <div>
              <span className="text-secondary-500">Телефон:</span>
              <span className="ml-2">{company.phone}</span>
            </div>
          )}
          {company.address && (
            <div>
              <span className="text-secondary-500">Адрес:</span>
              <span className="ml-2">{company.address}</span>
            </div>
          )}
        </div>
        {(company.inn || company.kpp || company.ogrn || company.legal_address) && <div className="mt-5 border-t border-secondary-100 pt-4"><h3 className="mb-2 font-semibold">Реквизиты компании</h3><div className="grid gap-2 text-sm text-secondary-600 sm:grid-cols-2">{company.inn && <p>ИНН: {company.inn}</p>}{company.kpp && <p>КПП: {company.kpp}</p>}{company.ogrn && <p>ОГРН: {company.ogrn}</p>}{company.legal_address && <p className="sm:col-span-2">Юридический адрес: {company.legal_address}</p>}</div></div>}
        {company.social_links && Object.entries(company.social_links).length > 0 && <div className="mt-4 flex flex-wrap gap-3 text-sm">{Object.entries(company.social_links).map(([name, url]) => <a key={name} className="text-primary-700 underline" href={String(url)} target="_blank" rel="noreferrer">{name}</a>)}</div>}
      </Card>

      {user?.id === company.owner_id && <>
        <CompanyTeamPanel companyId={company.id} />
        <ActiveSearchPanel companyId={company.id} vacancies={vacancies?.items || []} />
        <CandidateAlertSubscriptions />
      </>}

      {/* Vacancies */}
      <div id="company-vacancies">
        <h2 className="text-xl font-semibold mb-4">
          Вакансии компании ({vacancies?.total || 0})
        </h2>
        {vacancies?.items?.length > 0 ? (
          <div className="space-y-4">
            {vacancies.items.map((vacancy: any) => (
              <VacancyCard key={vacancy.id} vacancy={vacancy} />
            ))}
          </div>
        ) : (
          <p className="text-secondary-500 text-center py-8">
            В компании пока нет открытых вакансий
          </p>
        )}
      </div>

      <section className="mt-10" aria-labelledby="company-reviews-title">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="company-reviews-title" className="text-xl font-semibold">Отзывы о компании</h2>
            <p className="mt-1 text-sm text-secondary-600">Что говорят сотрудники и соискатели о взаимодействии с работодателем.</p>
          </div>
          {user?.role === 'candidate' && <Link to="/applications" className="rounded-md border border-primary-700 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50">Оставить отзыв</Link>}
        </div>
        {rating?.ratings_count > 0 && <Card className="mb-4 flex flex-wrap items-center gap-4 p-5">
          <div className="text-3xl font-bold">{rating.average_rating}</div>
          <div><div className="text-xl text-amber-500" aria-label={`Рейтинг ${rating.average_rating} из 5`}>{'★'.repeat(rating.stars)}{'☆'.repeat(5 - rating.stars)}</div><p className="text-sm text-secondary-500">{rating.ratings_count} {rating.ratings_count === 1 ? 'отзыв' : 'отзывов'}</p></div>
        </Card>}
        {rating?.reviews?.length ? <div className="space-y-3">{rating.reviews.map((review: any, index: number) => <Card key={`${review.created_at}-${index}`} className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-lg text-amber-500" aria-label={`Оценка ${review.rating} из 5`}>{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}</span><time className="text-sm text-secondary-500" dateTime={review.created_at}>{new Date(review.created_at).toLocaleDateString('ru-RU')}</time></div>
          {review.comment ? <p className="mt-3 whitespace-pre-wrap text-sm text-secondary-700">{review.comment}</p> : <p className="mt-3 text-sm italic text-secondary-500">Пользователь оставил оценку без комментария.</p>}
        </Card>)}</div> : <Card className="p-6 text-sm text-secondary-600">Пока нет опубликованных отзывов. Оценки появляются после завершённых этапов найма.</Card>}
        {user?.role === 'candidate' && <p className="mt-3 text-xs text-secondary-500">Оставить отзыв можно в разделе «Отклики» для компании, которая завершила рассмотрение заявки. Публикация происходит после встречной оценки или через 14 дней.</p>}
      </section>
    </div>
  )
}

export default CompanyDetail
